'use client';

import { observer } from 'mobx-react';
import { useEffect } from 'react';
import { showMessageModal } from '~/clientUtils/showMessageModal';
import Button from '~/components/commonUi/Button/Button';
import Input from '~/components/commonUi/Input/Input';
import { ZipCodeRange, ZipCodeRangeForService } from '~/definitions/zipcode';
import { getStore } from '~/mobx/AppStore';
import { ZipCodeParser } from '~/utils/zipCodeParser';

interface AddZipCodesProps {
    addZipcode: (zipCodesToAdd: ZipCodeRangeForService) => void;
    isSaveClicked: boolean;
    setIsSaveClicked: (isClicked: boolean) => void;
    errorKeys: string[];
    setErrorKeys: (keys: string[]) => void;
    addedData: ZipCodeRange;
    setAddedData: (data: ZipCodeRange) => void;
}

const AddZipCodes = (props: AddZipCodesProps): JSX.Element => {
    const hasStartError = props.errorKeys.includes('start');
    const hasEndError = props.errorKeys.includes('end');
    const shouldRenderError = !(hasStartError && hasEndError);

    const isStartError = shouldRenderError && hasStartError;
    const isEndError = shouldRenderError && hasEndError;

    const convertDataForService = (data: ZipCodeRange) => {
        return {
            start: parseInt(data.start),
            end: parseInt(data.end),
        };
    };

    const onChange = (key: string, event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        const updatedData = { ...props.addedData, [key]: value };
        props.setAddedData(updatedData);

        if (!props.isSaveClicked) {
            return;
        }

        if (!shouldRenderError) {
            props.setErrorKeys([]);
        }

        const validationResult = ZipCodeParser.validation(updatedData);

        const typedKey = key as keyof typeof validationResult;

        if (validationResult[typedKey]) {
            if (!props.errorKeys.includes(key)) {
                props.setErrorKeys([...props.errorKeys, key]);
            }
        } else {
            props.setErrorKeys(props.errorKeys.filter((e) => e !== key));
        }
    };

    const onClick = () => {
        const validationResult = ZipCodeParser.validation(props.addedData);

        const errorKeys = Object.keys(validationResult);

        if (errorKeys.length > 0) {
            props.setIsSaveClicked(true);
            const key = errorKeys[0] as keyof typeof validationResult;
            const message = validationResult[key];
            if (message) {
                showMessageModal('error', <div dangerouslySetInnerHTML={{ __html: message }} />);
                props.setErrorKeys(errorKeys);
                return;
            }
        }

        // No validation errors
        props.setErrorKeys([]);
        props.setIsSaveClicked(false);
        props.addZipcode(convertDataForService(props.addedData));
    };

    const classNamesContainerDefault = `flex flex-col mb-4`;
    const classNamesContainerDesktop = `md:flex-row md:items-center`;
    const classNamesContainer = [classNamesContainerDefault, classNamesContainerDesktop].join(' ');
    const classNamesLabelDefault = `font-normal flex flex-col mb-4 cursor-pointer`;
    const classNamesLabelDesktop = `md:flex-row md:items-center md:mb-0 md:mr-[16px]`;
    const classNamesLabel = [classNamesLabelDefault, classNamesLabelDesktop].join(' ');
    const classNamesTextDefault = `pr-[16px] mb-2`;
    const classNamesTextDesktop = `md:mb-0`;
    const classNamesText = [classNamesTextDefault, classNamesTextDesktop].join(' ');
    const classNamesInputDefault = `w-full`;
    const classNamesInputDesktop = `md:w-auto`;
    const classNamesInput = [classNamesInputDefault, classNamesInputDesktop].join(' ');

    return (
        <form method="POST" id="genZipcodeForm" name="genZipcodeForm">
            <div className={classNamesContainer}>
                <label className={classNamesLabel}>
                    <span className={`${classNamesText} ${isStartError ? 'text-[#a94442]' : ''}`}>
                        Fra<span className="text-red-700">*</span>:
                    </span>
                    <Input
                        name="from"
                        value={props.addedData.start}
                        testId={'from'}
                        extendedClassName={`${classNamesInput} ${isStartError ? 'border-[#a94442]' : ''}`}
                        onChange={(e) => onChange('start', e)}
                    />
                </label>
                <label className={classNamesLabel}>
                    <span className={`${classNamesText} ${isEndError ? 'text-[#a94442]' : ''}`}>Til:</span>
                    <Input
                        name="to"
                        value={props.addedData.end}
                        testId={'to'}
                        extendedClassName={`${classNamesInput} ${isEndError ? 'border-[#a94442]' : ''}`}
                        onChange={(e) => onChange('end', e)}
                    />
                </label>
                <Button btnText={'Lagre'} testId={'save-btn'} onClick={onClick} />
            </div>
        </form>
    );
};

const AddZipCodesWithStore = () => {
    const { zipcodeStore, regionStore } = getStore();

    const errorMessageContent = (text: string) => (
        <div className="items-center flex flex-col">
            <div>Følgende felfer er feil</div>
            <div>Kan ikke duplisere postnr i samme S-Lag: {text}</div>
            <div>Klikk på teksten for å fjerne denne meldingen.</div>
        </div>
    );

    const addZipcode = async (zipCodesToAdd: ZipCodeRangeForService) => {
        await zipcodeStore.getExistingZipcode();

        const duplicated = await zipcodeStore.checkDuplicatedZipcode(zipCodesToAdd);
        if (duplicated.length > 0) {
            showMessageModal('error', errorMessageContent(duplicated.join(', ')));
            zipcodeStore.setAddedData({ start: '', end: '' });
            return;
        }

        if (zipcodeStore.selectedZipcode === '') {
            const isAssigned = await zipcodeStore.checkAssignedZipcode();
            if (!isAssigned) {
                await zipcodeStore.assignZipcode();
            }
        }

        await zipcodeStore.addZipcode(zipCodesToAdd);
    };

    useEffect(() => {
        zipcodeStore.setIsSaveClicked(false);
        zipcodeStore.setErrorKeys([]);
        zipcodeStore.setAddedData({ start: '', end: '' });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [regionStore.selectedRegionId, zipcodeStore.selectedZipcode]);

    return (
        <AddZipCodes
            addZipcode={(zipCodesToAdd: ZipCodeRangeForService) => addZipcode(zipCodesToAdd)}
            isSaveClicked={zipcodeStore.isSaveClicked}
            setIsSaveClicked={(isClicked: boolean) => zipcodeStore.setIsSaveClicked(isClicked)}
            errorKeys={zipcodeStore.errorKeys}
            setErrorKeys={(keys: string[]) => zipcodeStore.setErrorKeys(keys)}
            addedData={zipcodeStore.addedData}
            setAddedData={(data: ZipCodeRange) => zipcodeStore.setAddedData(data)}
        />
    );
};

export default observer(AddZipCodesWithStore);
